import { clientLogger } from '@/lib/clientLogger';
import { useState, useEffect, useRef } from 'react';
import { useBackNavigationLayer } from '@/lib/navigation';
import { useParams } from 'react-router-dom';
import { Download, PenTool, CheckCircle, AlertCircle, Loader, DollarSign } from 'lucide-react';
import { ENV } from '@/config/environment';
import { SignaturePad } from './SignaturePad';
import { DepositDueNotice, PublicPaymentModal } from './PublicPaymentModal';
import { PaymentConfirmationBanner } from './PaymentConfirmationBanner';
import { usePaymentConfirmationPolling } from '@/hooks/usePaymentConfirmationPolling';
import { openDownloadUrl } from '@/lib/capacitor';

const API_BASE_URL = (ENV.API.BASE_URL || '').replace(/\/$/, '');
const PUBLIC_API_BASE_URL = API_BASE_URL.replace(/\/api$/i, '');

interface PublicInvoiceData {
  invoiceId: string;
  invoiceNumber: string;
  date: string;
  dueDate?: string;
  viewed?: boolean;
  signaturePath?: string;
  total?: number;
  amountPaid?: number;
  depositAmount?: number | null;
  paymentStatus?: string;
  canAcceptPayments: boolean;
  connectedAccountId?: string;
}

type PageState = 'loading' | 'error' | 'invoice' | 'signing' | 'completed';

export function PublicInvoicePage() {
  const { invoiceId, token } = useParams<{
    invoiceId: string;
    token: string;
  }>();

  const [state, setState] = useState<PageState>('loading');
  const [invoiceData, setInvoiceData] = useState<PublicInvoiceData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [signatureSuccessMessage, setSignatureSuccessMessage] = useState('');
  const [pdfRefreshKey, setPdfRefreshKey] = useState(0);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const hasMarkedViewed = useRef(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!invoiceId || !token) {
      setState('error');
      setErrorMessage('Invalid URL parameters');
      return;
    }

    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchInvoiceData();
  }, [invoiceId, token]);

  // Polling: check for backend payment confirmation after optimistic update
  const { banner: paymentBanner, startPolling } = usePaymentConfirmationPolling(
    `${PUBLIC_API_BASE_URL}/public/invoices/${invoiceId}/${token}`,
    'invoice',
    (raw) => {
      // Update with real backend data
      setInvoiceData({
        invoiceId: raw.invoice_id,
        invoiceNumber: raw.invoice_number,
        date: raw.date,
        dueDate: raw.due_date,
        viewed: raw.viewed,
        signaturePath: raw.signature_path || '',
        total: raw.total,
        amountPaid: raw.amount_paid,
        depositAmount: raw.deposit_amount,
        paymentStatus: raw.payment_status,
        canAcceptPayments: raw.can_accept_payments ?? false,
        connectedAccountId: raw.connected_account_id,
      });
      // Refresh PDF to show updated invoice
      setIsPdfLoading(true);
      setPdfRefreshKey((k) => k + 1);
    },
  );

  const fetchInvoiceData = async () => {
    try {
      setState('loading');

      const response = await fetch(`${PUBLIC_API_BASE_URL}/public/invoices/${invoiceId}/${token}`);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('This invoice link is invalid or has expired.');
        } else if (response.status === 404) {
          throw new Error('Invoice not found.');
        } else {
          throw new Error('Failed to load invoice. Please try again later.');
        }
      }

      // Normalize API response (snake_case) to UI shape (camelCase)
      const raw = await response.json();
      const data: PublicInvoiceData = {
        invoiceId: raw.invoice_id,
        invoiceNumber: raw.invoice_number,
        date: raw.date,
        dueDate: raw.due_date,
        viewed: raw.viewed,
        signaturePath: raw.signature_path || '',
        total: raw.total,
        amountPaid: raw.amount_paid,
        depositAmount: raw.deposit_amount,
        paymentStatus: raw.payment_status,
        canAcceptPayments: raw.can_accept_payments ?? false,
        connectedAccountId: raw.connected_account_id,
      };

      setInvoiceData(data);
      setState('invoice');

      if (!data.viewed) {
        await markAsViewed();
      }
      // Mark as viewed once

    } catch (error) {
      clientLogger.error('Error fetching invoice:', error);
      setState('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  const markAsViewed = async () => {
    if (hasMarkedViewed.current) {
      return;
    }

    hasMarkedViewed.current = true;

    try {
      await fetch(`${PUBLIC_API_BASE_URL}/public/invoices/${invoiceId}/${token}/viewed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      // Non-critical error, just log it and allow retry
      clientLogger.warn('Failed to mark invoice as viewed:', error);
      hasMarkedViewed.current = false;
    }
  };

  const getFileEndpointUrl = () => `${PUBLIC_API_BASE_URL}/public/invoices/${invoiceId}/${token}/file`;

  const handleDownload = () => {
    if (!invoiceData) return;

    // Prefer the secure file endpoint to avoid cross-origin issues
    const downloadUrl = getFileEndpointUrl();
    openDownloadUrl(downloadUrl);
  };

  const handleSignInvoice = () => {
    setShowSignaturePad(true);
  };

  const handleSignatureComplete = async (signaturePath: string, signerName: string) => {
    try {
      setState('signing');

      const response = await fetch(`${PUBLIC_API_BASE_URL}/public/invoices/${invoiceId}/${token}/signature`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signature_path: signaturePath, signed_by_name: signerName }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit signature');
      }

      // Stay on invoice screen after successful signature
      setState('invoice');
      setSignatureSuccessMessage('Thank you! Your signature has been submitted successfully.');
      setShowSignaturePad(false);
      setInvoiceData((prev) => (prev ? { ...prev, signaturePath } : prev));
      setIsPdfLoading(true); // Show loading while PDF regenerates with signature
      setPdfRefreshKey((k) => k + 1);
    } catch (error) {
      clientLogger.error('Error submitting signature:', error);
      setState('error');
      setErrorMessage('Failed to submit signature. Please try again.');
    }
  };

  const closeSignaturePad = useBackNavigationLayer({
    enabled: showSignaturePad,
    onBack: () => setShowSignaturePad(false)
  });

  const handleSignatureCancel = () => {
    closeSignaturePad();
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="mx-auto h-8 w-8 animate-spin text-blue-600 mb-4" />
          <p className="text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Invoice</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (state === 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Signed Successfully</h1>
          <p className="text-gray-600 mb-6">
            Thank you for signing this invoice. A confirmation has been sent to the invoice issuer.
          </p>
        </div>
      </div>
    );
  }

  if (!invoiceData) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {signatureSuccessMessage && state === 'invoice' && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              <span>{signatureSuccessMessage}</span>
            </div>
          </div>
        )}

        <PaymentConfirmationBanner banner={paymentBanner} />

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {invoiceData.canAcceptPayments && (invoiceData.total ?? 0) - (invoiceData.amountPaid ?? 0) > 0 && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-semibold text-white bg-purple-600 border border-purple-700 rounded-lg shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  Pay
                </button>
              )}
              <button
                onClick={handleSignInvoice}
                disabled={showSignaturePad}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <PenTool className="h-4 w-4" />
                Sign Invoice
              </button>
              <button
                onClick={handleDownload}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </button>
              
            </div>
          </div>
          {invoiceData.canAcceptPayments && (
            <DepositDueNotice
              total={invoiceData.total ?? 0}
              amountPaid={invoiceData.amountPaid ?? 0}
              depositAmount={invoiceData.depositAmount}
            />
          )}
        </div>

        {/* PDF Viewer */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Document</h2>
          <div className="w-full relative">
            {isPdfLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 rounded z-10">
                <Loader className="h-8 w-8 animate-spin text-blue-600 mb-2" />
                <span className="text-sm text-gray-600">Generating PDF...</span>
              </div>
            )}
            <iframe
              src={`${getFileEndpointUrl()}?r=${pdfRefreshKey}#navpanes=0&toolbar=0&view=FitH`}
              className="w-full h-[80vh] border border-gray-200 rounded"
              title="Invoice PDF"
              onLoad={() => setIsPdfLoading(false)}
              allowFullScreen
            />
          </div>
        </div>

        {/* Payment Modal */}
        {invoiceData.connectedAccountId && (
          <PublicPaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            createIntentUrl={`${PUBLIC_API_BASE_URL}/public/invoices/${invoiceId}/${token}/create-payment-intent`}
            connectedAccountId={invoiceData.connectedAccountId}
            totalAmount={invoiceData.total ?? 0}
            amountPaid={invoiceData.amountPaid ?? 0}
            depositAmount={invoiceData.depositAmount}
            documentLabel="Invoice"
            onSuccess={(paidAmount: number) => {
              setShowPaymentModal(false);

              // Optimistic update: immediately reflect payment in UI
              const newAmountPaid = Math.round(((invoiceData.amountPaid ?? 0) + paidAmount) * 100) / 100;
              const total = invoiceData.total ?? 0;
              const newStatus = newAmountPaid >= total ? 'PaidFully' : 'PartiallyPaid';

              setInvoiceData((prev) =>
                prev
                  ? { ...prev, amountPaid: newAmountPaid, paymentStatus: newStatus }
                  : prev
              );

              // Show processing banner and poll until the backend has the payment
              startPolling(invoiceData.amountPaid ?? 0);
            }}
          />
        )}

        {/* Signature Modal */}
        {showSignaturePad && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <SignaturePad
                onSignatureComplete={handleSignatureComplete}
                onCancel={handleSignatureCancel}
                initialSignaturePath={invoiceData.signaturePath}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
